import { Suspense, useEffect } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { Box, CircularProgress } from '@mui/material'
import Navigation from './Navigation'
import Footer from './Footer'
import Sidebar from './Sidebar'
import PushNotificationBanner from './PushNotificationBanner'
import PublicRecordingHeader from './PublicRecordingHeader'
import { LocationSettingsSync } from './HomeRedirect'
import { useAuth } from '../contexts/AuthContext'
import { isCliAuthPath, isPublicRecordingPath, needsTermsAcceptance, termsRedirectPath } from '../utils/publicRoutes'

function RoutePageFallback() {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '40vh' }}>
      <CircularProgress />
    </Box>
  )
}

interface AppLayoutProps {
  children: React.ReactNode
}

const AppLayout: React.FC<AppLayoutProps> = ({ children }) => {
  const { pathname, search } = useLocation()
  const navigate = useNavigate()
  const { user, isLoading } = useAuth()
  const publicRecording = isPublicRecordingPath(pathname)
  const needsTos = needsTermsAcceptance(user, pathname)
  const hideChromeForTerms = Boolean(user && !user.terms_accepted)
  const isBareHome = pathname === '/' || pathname === '/home'
  const isAuthChromeHidden =
    pathname === '/login' ||
    pathname === '/join' ||
    pathname === '/auth-bridge' ||
    isCliAuthPath(pathname) ||
    hideChromeForTerms ||
    (isBareHome && !user)

  useEffect(() => {
    if (isLoading || !needsTos) return
    navigate(termsRedirectPath(search), { replace: true })
  }, [isLoading, needsTos, search, navigate])

  if (!isLoading && needsTos) {
    return (
      <Box
        sx={{
          height: '100dvh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: 'var(--vs-bg)',
        }}
      >
        <CircularProgress />
      </Box>
    )
  }

  return (
    <Box
      sx={{
        height: '100dvh',
        minHeight: 0,
        bgcolor: isAuthChromeHidden ? 'var(--vs-bg)' : 'background.default',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      <LocationSettingsSync />
      {publicRecording ? (
        <PublicRecordingHeader />
      ) : !isAuthChromeHidden ? (
        <>
          <Navigation />
          <PushNotificationBanner />
        </>
      ) : null}
      <Box sx={{ display: 'flex', flex: 1, minHeight: 0, overflow: 'hidden' }}>
        {!publicRecording && !isAuthChromeHidden && <Sidebar />}
        <Box
          component="main"
          sx={{
            flex: 1,
            minWidth: 0,
            minHeight: 0,
            overflow: 'auto',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Box sx={{ flex: '1 0 auto' }}>
            <Suspense fallback={<RoutePageFallback />}>{children}</Suspense>
          </Box>
          {!isAuthChromeHidden && <Footer />}
        </Box>
      </Box>
    </Box>
  )
}

export default AppLayout
